import { collection, addDoc, getDocs, doc, getDoc, updateDoc, deleteDoc, Timestamp, orderBy, query } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { CriteriaGroup } from '@/features/PerformanceCriteria/services/criteriaService';

export type QuestionnaireType = 'PERFORMANCE' | 'CLIMATE' | 'OTHER';

export interface Questionnaire {
    id?: string;
    title: string;
    description: string;
    type: QuestionnaireType;
    introductionCollaborator?: string;
    introductionLeader?: string;
    conclusionCollaborator?: string;
    conclusionLeader?: string;
    criteria: CriteriaGroup[]; // Nested criteria groups
    createdAt?: Date;
    updatedAt?: Date;
}

const COLLECTION_NAME = 'questionnaires';

export const getQuestionnaires = async (): Promise<Questionnaire[]> => {
    try {
        const q = query(collection(db, COLLECTION_NAME), orderBy('title'));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => {
            const data = doc.data();
            return {
                id: doc.id,
                ...data,
                createdAt: data.createdAt?.toDate(),
                updatedAt: data.updatedAt?.toDate()
            } as Questionnaire;
        });
    } catch (error) {
        console.error("Error fetching questionnaires", error);
        throw error;
    }
};

export const getQuestionnaireById = async (id: string): Promise<Questionnaire | null> => {
    try {
        const docRef = doc(db, COLLECTION_NAME, id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            const data = docSnap.data();
            return {
                id: docSnap.id,
                ...data,
                createdAt: data.createdAt?.toDate(),
                updatedAt: data.updatedAt?.toDate()
            } as Questionnaire;
        } else {
            return null;
        }
    } catch (error) {
        console.error("Error fetching questionnaire by id", error);
        throw error;
    }
};

export const saveQuestionnaire = async (questionnaire: Questionnaire): Promise<string> => {
    try {
        const { id, ...data } = questionnaire;
        const payload = {
            ...data,
            updatedAt: Timestamp.now(),
        };

        if (id) {
            const docRef = doc(db, COLLECTION_NAME, id);
            await updateDoc(docRef, payload);
            return id;
        } else {
            const docRef = await addDoc(collection(db, COLLECTION_NAME), {
                ...payload,
                createdAt: Timestamp.now()
            });
            return docRef.id;
        }
    } catch (error) {
        console.error("Error saving questionnaire", error);
        throw error;
    }
};

export const deleteQuestionnaire = async (id: string): Promise<void> => {
    try {
        await deleteDoc(doc(db, COLLECTION_NAME, id));
    } catch (error) {
        console.error("Error deleting questionnaire", error);
        throw error;
    }
};
